import { Button, ConfirmationDialog } from '@systemeio/components'
import { BadRequest, InternalError, UnprocessableContentError } from '@systemeio/util'
import { useState, type JSX } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { FieldErrorAndDescription } from '../form/field-error-and-description'
import { toast } from '../toast'

export interface ConfirmationProps {
  opened: boolean
  onClose: () => void
  onConfirm: () => Promise<void> | void
  onDecline?: () => Promise<void> | void
  title?: string
  confirmationText?: string | JSX.Element
  toastCaption?: string | JSX.Element
  confirmButtonCaption?: string
  cancelButtonCaption?: string
  confirmationContent?: JSX.Element
  afterLeave?: () => void
  disabledConfirmButton?: boolean
  status?: ConfirmationDialog.Popup.Status
}

const defaultTitle = 'confirmation_modal.default_title'

function Confirmation({
  opened,
  onClose,
  onConfirm,
  onDecline,
  title,
  confirmationText = '',
  toastCaption,
  cancelButtonCaption,
  confirmButtonCaption,
  confirmationContent,
  afterLeave,
  disabledConfirmButton,
  status,
}: ConfirmationProps) {
  const { t } = useLocoTranslation()
  const [error, setError] = useState('')
  const [isFetching, setIsFetching] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)

  const handleConfirm = async () => {
    try {
      setIsFetching(true)
      await onConfirm()
      setIsFetching(false)
      setIsConfirmed(true)
      onClose()
      if (toastCaption) {
        toast.success(toastCaption)
      }
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        if (e.errors.common) {
          setError(e.errors.common.join(''))
        }
      } else if (e instanceof UnprocessableContentError) {
        // do nothing while we're migrating 400 errors
      } else if (e instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  const handleAfterLeave = () => {
    afterLeave?.()
    setError('')
    setIsConfirmed(false)
  }

  return (
    <ConfirmationDialog.Root
      open={opened}
      onOpenChange={(open, eventDetails) => {
        if (open) return
        if (isFetching) {
          eventDetails.cancel()
          return
        }
        if (!isConfirmed) void onDecline?.()
        onClose()
      }}
      onOpenChangeComplete={open => {
        if (!open) handleAfterLeave()
      }}
    >
      <ConfirmationDialog.Popup
        actions={
          <>
            <ConfirmationDialog.Close
              render={<Button disabled={isFetching} type="button" variant="secondary" />}
            >
              {cancelButtonCaption || t('confirmation_modal.cancel')}
            </ConfirmationDialog.Close>
            <Button
              type="submit"
              disabled={disabledConfirmButton || isFetching}
              isLoading={isFetching}
              variant={status === 'error' ? 'destructive' : 'primary'}
            >
              {confirmButtonCaption || t('confirmation_modal.confirm')}
            </Button>
          </>
        }
        closeProps={{ disabled: isFetching }}
        description={confirmationText}
        feedback={<FieldErrorAndDescription error={error} errorClassName="text-center" />}
        render={
          <form
            onSubmit={async event => {
              event.preventDefault()
              await handleConfirm()
            }}
          />
        }
        status={status}
        title={title || t(defaultTitle)}
      >
        {confirmationContent}
      </ConfirmationDialog.Popup>
    </ConfirmationDialog.Root>
  )
}

export default Confirmation
